import { useId, type ReactNode } from 'react';

/**
 * Form fields of Trace Admin: a visible label, an optional hint and an error that is announced with the
 * field (aria-describedby, aria-invalid). The library has no form controls yet, so these are app-owned and
 * styled in admin.css (admin-field, admin-input).
 */

interface FieldFrameProps {
  id: string;
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: (describedBy: string | undefined) => ReactNode;
}

function FieldFrame({ id, label, hint, error, children }: FieldFrameProps) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="admin-field" data-invalid={error ? 'true' : undefined}>
      <label className="admin-field__label" htmlFor={id}>
        {label}
      </label>
      {children(describedBy)}
      {hint ? (
        <p className="admin-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="admin-field__error" id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  error?: string | null;
  type?: 'text' | 'email' | 'date' | 'number' | 'search';
  maxLength?: number;
  min?: number | string;
  max?: number | string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email';
  spellCheck?: boolean;
}

export function TextField({ label, value, onChange, hint, error, type = 'text', ...rest }: TextFieldProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error ?? null}>
      {(describedBy) => (
        <input
          id={id}
          className="admin-input"
          type={type}
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      )}
    </FieldFrame>
  );
}

export interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  error?: string | null;
  maxLength?: number;
  rows?: number;
  required?: boolean;
}

export function TextAreaField({
  label,
  value,
  onChange,
  hint,
  error,
  rows = 3,
  ...rest
}: TextAreaFieldProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error ?? null}>
      {(describedBy) => (
        <textarea
          id={id}
          className="admin-input admin-input--area"
          value={value}
          rows={rows}
          onChange={(event) => onChange(event.currentTarget.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      )}
    </FieldFrame>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
}

export function SelectField({ label, value, onChange, options, hint, error, required }: SelectFieldProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error ?? null}>
      {(describedBy) => (
        <select
          id={id}
          className="admin-input"
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          required={required}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldFrame>
  );
}

export interface Choice {
  value: string;
  label: string;
  description?: string;
}

interface ChoiceGroupProps {
  legend: string;
  choices: readonly Choice[];
  hint?: ReactNode;
  error?: string | null;
}

function ChoiceGroup({
  legend,
  hint,
  error,
  children,
}: ChoiceGroupProps & { children: (describedBy: string | undefined) => ReactNode }) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <fieldset
      className="admin-choices"
      aria-describedby={describedBy}
      data-invalid={error ? 'true' : undefined}
    >
      <legend className="admin-field__label">{legend}</legend>
      {hint ? (
        <p className="admin-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {children(describedBy)}
      {error ? (
        <p className="admin-field__error" id={errorId}>
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

/** One choice among several (radio buttons), each with an optional description. */
export function RadioGroup({
  value,
  onChange,
  ...group
}: ChoiceGroupProps & { value: string; onChange: (value: string) => void }) {
  const name = useId();
  return (
    <ChoiceGroup {...group}>
      {() =>
        group.choices.map((choice) => {
          const id = `${name}-${choice.value}`;
          return (
            <div className="admin-choice" key={choice.value}>
              <input
                id={id}
                type="radio"
                name={name}
                value={choice.value}
                checked={value === choice.value}
                onChange={() => onChange(choice.value)}
                aria-describedby={choice.description ? `${id}-description` : undefined}
              />
              <label htmlFor={id}>
                {choice.label}
                {choice.description ? (
                  <span className="admin-choice__description" id={`${id}-description`}>
                    {choice.description}
                  </span>
                ) : null}
              </label>
            </div>
          );
        })
      }
    </ChoiceGroup>
  );
}

/** Any number of choices (check boxes). */
export function CheckboxGroup({
  values,
  onChange,
  ...group
}: ChoiceGroupProps & { values: readonly string[]; onChange: (values: string[]) => void }) {
  const name = useId();
  const toggle = (value: string, checked: boolean) =>
    onChange(
      checked
        ? group.choices.map((c) => c.value).filter((v) => v === value || values.includes(v))
        : values.filter((v) => v !== value),
    );
  return (
    <ChoiceGroup {...group}>
      {() =>
        group.choices.map((choice) => {
          const id = `${name}-${choice.value}`;
          return (
            <div className="admin-choice" key={choice.value}>
              <input
                id={id}
                type="checkbox"
                value={choice.value}
                checked={values.includes(choice.value)}
                onChange={(event) => toggle(choice.value, event.currentTarget.checked)}
              />
              <label htmlFor={id}>
                {choice.label}
                {choice.description ? (
                  <span className="admin-choice__description">{choice.description}</span>
                ) : null}
              </label>
            </div>
          );
        })
      }
    </ChoiceGroup>
  );
}

/** The bounds of every reason platform-api records (reasonBodySchema and the request schemas: 3 to 500). */
export const REASON_MIN = 3;
export const REASON_MAX = 500;

/** The reason of a change, recorded in the audit log with the staff member's name. Required unless optional. */
export function ReasonField({
  value,
  onChange,
  error,
  label = 'Reason',
  optional = false,
  min = REASON_MIN,
  max = REASON_MAX,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  label?: string;
  optional?: boolean;
  min?: number;
  max?: number;
}) {
  const bounds = `${min} to ${max} characters.`;
  return (
    <TextAreaField
      label={label}
      value={value}
      onChange={onChange}
      error={error ?? null}
      maxLength={max}
      required={!optional}
      hint={
        optional
          ? `Optional. When given, it is recorded in the audit log with your name: ${bounds}`
          : `Recorded in the audit log with your name. ${bounds}`
      }
    />
  );
}

/** The error of a reason, or null when it is long enough (counted without surrounding spaces). */
export function reasonError(value: string, min = REASON_MIN, max = REASON_MAX): string | null {
  const length = value.trim().length;
  if (length === 0) return 'Enter a reason.';
  if (length < min) return `Write at least ${min} characters.`;
  if (length > max) return `Write at most ${max} characters.`;
  return null;
}
